import { createPortal } from "react-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, FileText, Route, LayoutDashboard, ChevronRight } from "lucide-react";

// Admin dashboard tab catalogue — kept in sync with pages/Admin.jsx `groups`.
// Each entry becomes a jumpable item when the palette is opened from anywhere.
const ADMIN_TABS = [
  { group: "Home",            value: "dashboard",        label: "Dashboard home" },
  { group: "Content",         value: "upload",           label: "Upload blog" },
  { group: "Content",         value: "pathways",         label: "Pathways" },
  { group: "Content",         value: "glossary",         label: "Glossary" },
  { group: "Content",         value: "trash",            label: "Trash" },
  { group: "People & data",   value: "authors",          label: "Authors" },
  { group: "People & data",   value: "comments",         label: "Comments" },
  { group: "People & data",   value: "feedback",         label: "Feedback" },
  { group: "People & data",   value: "users",            label: "Users" },
  { group: "People & data",   value: "analytics",        label: "Analytics" },
  { group: "Site & Editors",  value: "search",           label: "Search settings" },
  { group: "Site & Editors",  value: "page-visibility",  label: "Page visibility" },
  { group: "Site & Editors",  value: "home",             label: "Home page visual editor" },
  { group: "Site & Editors",  value: "bookmarks-intro",  label: "Bookmarks visual editor" },
  { group: "Site & Editors",  value: "glossary-intro",   label: "Glossary visual editor" },
  { group: "Site & Editors",  value: "tools-and-metrics",label: "Tools and Metrics visual editor" },
  { group: "Site & Editors",  value: "pathway-intros",   label: "Pathways visual editor" },
  { group: "Site & Editors",  value: "blog-display",     label: "Blog display settings" },
  { group: "Site & Editors",  value: "visual-editor",    label: "Global section editor" },
  { group: "Site & Editors",  value: "shortcuts",        label: "Keyboard shortcuts" },
  { group: "Site & Editors",  value: "editor-ui",        label: "Blog editor workspace" },
  { group: "Site & Editors",  value: "footer",           label: "Footer & Socials" },
];

// Non-fancy fuzzy score. Higher is better; -1 means no match. Prioritises
// exact substring, then title-start prefix, then out-of-order character
// matches. Good enough for a few hundred rows and zero deps.
function score(query, target) {
  if (!query) return 0;
  const q = query.trim().toLowerCase();
  const t = (target || "").toLowerCase();
  if (!q) return 0;
  if (!t) return -1;
  const idx = t.indexOf(q);
  if (idx === 0) return 1000 - t.length;
  if (idx > 0) return 500 - idx;
  // Out-of-order character walk
  let ti = 0, matched = 0;
  for (const ch of q) {
    const at = t.indexOf(ch, ti);
    if (at === -1) return -1;
    matched += 1;
    ti = at + 1;
  }
  return matched * 10 - (ti - matched);
}

const ICONS = {
  tab:     LayoutDashboard,
  blog:    FileText,
  pathway: Route,
};

/**
 * Global command palette. Renders nothing until `open` is toggled to true.
 * Fetches its data (blogs + pathways) the first time it opens and caches it
 * in module-scoped state (so re-opening is instant). Keyboard-driven:
 * ↑↓ navigate, Enter selects, Esc closes. Rendered via a portal so it can
 * layer above any modal (Blog Editor, admin dialogs, etc.).
 */
export default function CommandPalette({ open, onClose }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [blogs, setBlogs] = useState([]);
  const [pathways, setPathways] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Lazy-fetch data on first open — auth is admin-only, so hitting these
  // endpoints without an admin session returns filtered results (fine).
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setSelected(0);
    // Focus after the modal mounts
    setTimeout(() => inputRef.current?.focus(), 30);
    if (blogs.length && pathways.length) return; // cached
    setLoading(true);
    Promise.all([
      api.get("/blogs").then(r => r.data).catch(() => []),
      api.get("/pathways?include_hidden=true").then(r => r.data).catch(() => []),
    ]).then(([b, p]) => {
      setBlogs(b);
      setPathways(p);
    }).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Flatten data + admin tabs into a single scored list.
  const items = useMemo(() => {
    const raw = [
      ...ADMIN_TABS.map(t => ({
        kind: "tab", group: t.group, label: t.label, subtitle: `Admin · ${t.group}`,
        onSelect: () => navigate(`/admin?tab=${encodeURIComponent(t.value)}`),
      })),
      ...blogs.map(b => ({
        kind: "blog", group: "Blogs",
        label: b.title || "Untitled",
        subtitle: b.author_name ? `Blog · ${b.author_name}` : "Blog",
        onSelect: () => navigate(`/blogs/${b.slug}`),
      })),
      ...pathways.map(p => ({
        kind: "pathway", group: "Pathways",
        label: p.title || "Untitled pathway",
        subtitle: p.visible === false ? "Pathway · Hidden" : "Pathway",
        onSelect: () => navigate(`/pathways/${p.slug}`),
      })),
    ];
    if (!query.trim()) {
      // Default view: show admin tabs first, then a handful of blogs/pathways.
      return raw.slice(0, 60);
    }
    return raw
      .map(it => ({ it, s: Math.max(score(query, it.label), score(query, it.subtitle) - 50) }))
      .filter(x => x.s >= 0)
      .sort((a, b) => b.s - a.s)
      .slice(0, 60)
      .map(x => x.it);
  }, [query, blogs, pathways, navigate]);

  useEffect(() => { setSelected(0); }, [query]);

  // Keep the selected row scrolled into view.
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${selected}"]`);
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [selected]);

  const commit = (idx) => {
    const it = items[idx];
    if (!it) return;
    onClose();
    it.onSelect();
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelected(s => Math.min(items.length - 1, s + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelected(s => Math.max(0, s - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      commit(selected);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      data-testid="command-palette-backdrop"
      className="fixed inset-0 z-[9998] bg-slate-900/40 backdrop-blur-sm grid place-items-start pt-[10vh] p-4"
      onClick={onClose}
    >
      <div
        data-testid="command-palette"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl bg-white rounded-xl shadow-2xl ring-1 ring-slate-200 overflow-hidden"
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
          <Search className="w-4 h-4 text-slate-400" />
          <input
            ref={inputRef}
            data-testid="command-palette-input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKey}
            placeholder="Search blogs, pathways, admin tabs…"
            className="flex-1 outline-none text-sm placeholder:text-slate-400"
          />
          <kbd className="text-[10px] uppercase tracking-widest text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">Esc</kbd>
        </div>
        <div
          ref={listRef}
          data-testid="command-palette-list"
          className="max-h-[60vh] overflow-y-auto py-1"
        >
          {loading && items.length === 0 && (
            <div className="px-4 py-6 text-sm text-slate-400">Loading…</div>
          )}
          {!loading && items.length === 0 && (
            <div className="px-4 py-6 text-sm text-slate-400 italic">
              No matches for "{query}".
            </div>
          )}
          {items.map((it, idx) => {
            const Icon = ICONS[it.kind] || FileText;
            const active = idx === selected;
            const prev = idx > 0 ? items[idx - 1] : null;
            const showGroupHeader = !query.trim() && (!prev || prev.group !== it.group);
            return (
              <div key={`${it.kind}-${idx}`}>
                {showGroupHeader && (
                  <div className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                    {it.group}
                  </div>
                )}
                <button
                  data-idx={idx}
                  data-testid={`command-palette-item-${idx}`}
                  onMouseEnter={() => setSelected(idx)}
                  onClick={() => commit(idx)}
                  className={`w-full text-left flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                    active
                      ? "bg-sky-50 text-sky-900"
                      : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <Icon className={`w-4 h-4 shrink-0 ${active ? "text-sky-700" : "text-slate-400"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{it.label}</div>
                    <div className={`text-[11px] truncate ${active ? "text-sky-700" : "text-slate-400"}`}>{it.subtitle}</div>
                  </div>
                  {active && <ChevronRight className="w-4 h-4 text-sky-700 shrink-0" />}
                </button>
              </div>
            );
          })}
        </div>
        <div className="px-4 py-2 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-400">
          <div className="flex items-center gap-3">
            <span><kbd className="bg-slate-100 px-1.5 py-0.5 rounded">↑</kbd> <kbd className="bg-slate-100 px-1.5 py-0.5 rounded">↓</kbd> to navigate</span>
            <span><kbd className="bg-slate-100 px-1.5 py-0.5 rounded">↵</kbd> to open</span>
          </div>
          <span>{items.length} result{items.length === 1 ? "" : "s"}</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
